import type { PrListBody, QueueBody, ReviewQueueBody } from '../../shared/pr/api';
import type { AppRole } from '../../shared/roles';
import type { SupplierListBody } from '../../shared/suppliers/review';
import type { MessageKey } from '../i18n';
import { apiGet } from './api';

// What waits on the signed-in person right now (D91): live counts from the lists they already
// work from, for the header bell. Nothing is stored; a list that fails to load is left out.

export interface Alert {
  key: MessageKey;
  count: number;
  href: string;
  /** Overdue or blocking: the bell turns red. */
  urgent?: boolean;
}

export async function attentionFor(
  roles: readonly AppRole[],
  poApprover = false,
): Promise<Alert[]> {
  const jobs: Promise<Alert[]>[] = [
    // Everyone is a requester: drafts not sent and requests returned to them.
    apiGet<PrListBody>('/api/prs').then((b) => [
      {
        key: 'bell.returned',
        count: b.requests.filter((r) => r.status === 'Returned').length,
        href: '#/requests',
        urgent: true,
      },
      {
        key: 'bell.drafts',
        count: b.requests.filter((r) => r.status === 'Draft').length,
        href: '#/requests',
      },
    ]),
    // Department heads (and backups): requests Procurement sent them. Empty for everyone else.
    apiGet<ReviewQueueBody>('/api/prs/for-decision').then((b) => [
      { key: 'bell.headDecide', count: b.items.length, href: '#/', urgent: true },
    ]),
  ];
  if (roles.includes('Procurement')) {
    jobs.push(
      apiGet<QueueBody>('/api/queue').then((q) => [
        { key: 'bell.toReview', count: q.counts.toReview, href: '#/review' },
        { key: 'bell.pastTarget', count: q.counts.pastTarget, href: '#/queue', urgent: true },
      ]),
      // Supplier registrations sent in and waiting for Procurement's review (D108).
      apiGet<SupplierListBody>('/api/suppliers').then((b) => [
        {
          key: 'bell.suppliers',
          count: b.suppliers.filter((x) => x.status === 'submitted').length,
          href: '#/suppliers',
        },
      ]),
    );
  }
  // The CFO, and a vertical's PO approver after the CFO (D114).
  if (roles.includes('CFO') || poApprover) {
    jobs.push(
      apiGet<{ items: unknown[] }>('/api/po-approvals').then((b) => [
        { key: 'bell.cfo', count: b.items.length, href: '#/po-approvals', urgent: true },
      ]),
    );
  }
  if (roles.includes('Finance')) {
    jobs.push(
      apiGet<{ toReceive: unknown[]; toPay: unknown[] }>('/api/finance/inbox').then((b) => [
        { key: 'bell.toReceive', count: b.toReceive.length, href: '#/payments' },
        { key: 'bell.toPay', count: b.toPay.length, href: '#/payments' },
      ]),
    );
  }
  const settled = await Promise.allSettled(jobs);
  return settled
    .flatMap((r) => (r.status === 'fulfilled' ? r.value : []))
    .filter((a) => a.count > 0);
}

export const totalOf = (alerts: readonly Alert[]) => alerts.reduce((n, a) => n + a.count, 0);
